import type { OssImageParams, SupportedImageFormat } from '../components/img';

// 检查是否为支持的图片格式
export function isSupportedImageFormat(url: string): boolean {
  if (typeof url !== 'string') return false;
  if (!url) return false;
  const extension = url.split('?')[0].split('.').pop()?.toLowerCase();
  if (!extension) return false;

  const supportedFormats: SupportedImageFormat[] = ['png', 'jpg', 'jpeg'];
  return supportedFormats.includes(extension as SupportedImageFormat);
}

// 检查是否为视频文件
export function isVideoFile(url: string): boolean {
  if (!url) return false;

  const extension = url.split('?')[0].split('.').pop()?.toLowerCase();
  if (!extension) return false;

  const videoExtensions = ['mp4', 'mov', 'avi', 'mpeg', 'wmv', 'rmvb', 'webm'];
  return videoExtensions.includes(extension);
}

// 从 URL 中提取现有的 OSS 处理参数
export function extractOssProcess(url: string): string {
  const match = url.match(/x-oss-process=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

// 构建 OSS 图片处理参数
export function buildOssProcessParams(params: OssImageParams): string {
  const processes: string[] = [];

  // 裁剪
  if (params.crop) {
    const { x, y, width, height } = params.crop;
    processes.push(`crop,x_${x},y_${y},w_${width},h_${height}`);
  }

  // 调整大小
  if (params.width || params.height) {
    const resizeParams: string[] = [];
    if (params.width) resizeParams.push(`w_${params.width}`);
    if (params.height) resizeParams.push(`h_${params.height}`);
    resizeParams.push('m_lfit'); // 默认使用 lfit 模式
    processes.push(`resize,${resizeParams.join(',')}`);
  }

  // 旋转
  if (params.rotate) {
    processes.push(`rotate,${params.rotate}`);
  }

  // 翻转
  if (params.flip) {
    processes.push(`flip,${params.flip === 'horizontal' ? 'h' : 'v'}`);
  }

  // 模糊
  if (params.blur) {
    processes.push(`blur,r_${params.blur},s_${params.blur}`);
  }

  // 锐化
  if (params.sharpen) {
    processes.push(`sharpen,${params.sharpen}`);
  }

  // 格式转换和质量
  const formatParams: string[] = [];

  if (params.format) {
    formatParams.push(params.format);
  } else {
    formatParams.push('webp');
  }

  if (params.quality) {
    formatParams.push(`q_${params.quality}`);
  }

  if (formatParams.length > 0) {
    processes.push(`format,${formatParams.join(',')}`);
  }

  return processes.length > 0 ? `image/${processes.join('/')}` : '';
}

// 检查是否为本地 URL (localhost, 127.0.0.1, 局域网 IP, 或者 @fs 本地文件路径)
export function isLocalUrl(url: string): boolean {
  if (!url) return false;
  // 1. 检查是否包含 @fs (Vite 开发服务器文件系统路径)
  if (url.includes('/@fs/')) return true;

  // 2. 检查是否为本地 IP 或域名
  return /(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+|10\.\d+\.\d+\.\d+)(:\d+)?/.test(url);
}

// 转换图片 URL 为 OSS 处理后的 URL
export function transformImageUrl(url: string, params?: OssImageParams): string {
  if (!url) return '';

  // 检查是否不是 png 和 jpg 图片，这些不需要格式转换
  if (!isSupportedImageFormat(url) || isLocalUrl(url)) {
    return url;
  }

  try {
    // 解析 URL
    const hasOrigin = url.startsWith('http');
    const baseUrl = hasOrigin ? url : `//example.com${url.startsWith('/') ? '' : '/'}${url}`;
    const parseUrl = new URL(baseUrl);

    // 获取现有的处理参数
    const existingProcess = extractOssProcess(url);

    // 构建新的处理参数
    let ossParams = '';
    if (params && Object.keys(params).length > 0) {
      ossParams = buildOssProcessParams(params);
    } else {
      ossParams = 'image/format,webp';
    }

    // 合并处理参数
    if (existingProcess) {
      // 如果已有处理参数，添加到现有参数后面
      ossParams = `${existingProcess}/${ossParams.replace('image/', '')}`;
    }

    // 设置或更新处理参数
    parseUrl.searchParams.set('x-oss-process', ossParams);

    // 返回处理后的 URL
    return hasOrigin
      ? parseUrl.toString()
      : `${parseUrl.pathname}?${parseUrl.searchParams.toString()}`;
  } catch (_error) {
    // 在生产环境中可能需要上报错误
    return url;
  }
}

// 获取图片的默认 OSS 处理参数
export function getDefaultOssParams(): OssImageParams {
  return {
    format: 'webp',
    quality: 100,
  };
}
